<template>
  <!-- ATTRACT — one row of Home's Store-health scorecard (HealthRow). -->
  <div class="gauge-row">
    <HealthRow
      :eyebrow="$t('gauges.eyebrow.attract')"
      :subtitle="$t('gauges.subtitle.attract')"
      :accent="accent"
      :disabled="cardDisabled"
      :status="rowStatus"
      :value="`${currency} ${formatMoney(displayEuro)}`"
      :value-sub="climbEuroSub"
      :value-est="true"
      :value-unit="$t('gauges.row.perMonth')"
      :meta="rowMeta"
      :pct="headlinePctVal"
      :pct-sub="climbCopy"
      :delta="deltaLabel"
      bar="ladder"
      :rungs="rowRungs"
      :axis-max="rowAxisMax"
      @help="showEfficiencyHelp = true"
    />
    <StoreCommercialEfficiencyHelpModal
      :show="showEfficiencyHelp"
      :segment-key="segmentKey"
      @close="showEfficiencyHelp = false"
    />
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import StoreCommercialEfficiencyHelpModal from './StoreCommercialEfficiencyHelpModal.vue'
import HealthRow from './ui/HealthRow.vue'
import { computeStoreEfficiency } from '@/prioritization/storeEfficiency'
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'

export default {
  name: 'StoreEfficiencyGauge',
  components: { StoreCommercialEfficiencyHelpModal, HealthRow },
  emits: ['axis-top'],
  props: {
    fixedWidgetsData: { type: Object, required: true },
    fixedWidgetsCompareToData: { type: Object, default: null },
    segmentKey: { type: String, default: 'default' },
    // Per-zone override + self-calibration map (buildZoneOverridesMap) — plumbed
    // through so the ladder ratchets with each zone's demonstrated 90-day potential.
    zoneOverrides: { type: Object, default: () => ({}) },
    compareToLabel: { type: String, default: 'period' },
    isActive: { type: Boolean, default: true },
    accent: { type: String, default: '#36910d' },
    hasProductZones: { type: Boolean, default: true },
    // True when at least one product zone has a polygon — engagement / conversion
    // math needs polygon-tracked visit durations. False → "not measurable" state.
    hasMeasurableProductZones: { type: Boolean, default: true },
    productZoneCount: { type: Number, default: 0 },
    currency: { type: String, default: '€' },
    // The canonical store within-reach €/mo (Σ per-zone cards). When provided it
    // becomes the gauge's € readout — this is the "commercial efficiency" gauge,
    // so the total commercial € within reach is exactly the right headline, and
    // it matches the Today hero + the per-zone cards. Null → own-best climb gap.
    withinReachEur: { type: Number, default: null },
    // A scale shared with the other ladder rows so "your best" lines up across them. Null → this gauge's own scale. Its own need is emitted as
    // `axis-top` (the highest % it has to show) so the host can pick the shared max.
    axisMax: { type: Number, default: null },
  },
  setup(props, { emit }) {
    const { t } = useI18n()
    const showEfficiencyHelp = ref(false)
    const hasProductZones = computed(() => !!props.hasProductZones)

    // Store totals — shared math layer (one source of truth with the Today hero).
    // Honours per-zone sector overrides + self-calibrated own-best + the peer cohort.
    const storeTotals = computed(() =>
      computeStoreEfficiency(
        props.fixedWidgetsData,
        props.segmentKey,
        props.zoneOverrides,
        props.fixedWidgetsData?._cohort ?? null,
      )
    )
    const compareToTotals = computed(() =>
      props.fixedWidgetsCompareToData
        ? computeStoreEfficiency(
            props.fixedWidgetsCompareToData,
            props.segmentKey,
            props.zoneOverrides,
            props.fixedWidgetsData?._cohort ?? null,
          )
        : null
    )

    // platform_capabilities.md §17.7 — the backend's own "engagement not measurable" verdict for a
    // footfall (all-tripwire) store. Redundant with hasMeasurableProductZones
    // (derived from the DetectionZones geometry) but never weakens it.
    const signalNotMeasurable = computed(() => storeTotals.value.notMeasurable === true)

    // --- four-rung climb geometry ---
    const headlinePctVal = computed(() => Math.round(storeTotals.value.headlinePct || 0))
    const peerTick = computed(() => {
      const v = storeTotals.value.peerTickPct
      return Number.isFinite(v) ? v : null
    })
    const ceilingTick = computed(() => {
      const v = storeTotals.value.ceilingTickPct
      return Number.isFinite(v) ? v : null
    })
    const climbAxisMax = computed(() => {
      const top = Math.max(100, headlinePctVal.value, ceilingTick.value ?? 0, peerTick.value ?? 0)
      return Math.max(100, Math.ceil((top * 1.06) / 10) * 10)
    })

    // Ladder-aware copy + € target: the message shifts with where you sit on the
    // climb; the € always targets the NEXT rung you haven't reached.
    const activeRung = computed(() => {
      const t = storeTotals.value
      const hp = t.headlinePct || 0
      const rungs = [{ key: 'best', pct: 100, total: t.ownBestTotal }]
      if (t.peerTickPct != null) rungs.push({ key: 'peers', pct: t.peerTickPct, total: t.cohortTotal })
      if (t.ceilingTickPct != null) rungs.push({ key: 'bic', pct: t.ceilingTickPct, total: t.ceilingTotal })
      const next = rungs.find(r => r.pct > hp + 2) || null
      return { hp, rungs, next, realized: t.realizedTotal || 0 }
    })
    const climbCopy = computed(() => {
      const { hp, rungs, next } = activeRung.value
      const at = rungs.find(r => Math.abs(r.pct - hp) <= 2)
      if (at) return at.key === 'best' ? t('gauges.attract.climb.atBest')
        : at.key === 'peers' ? t('gauges.attract.climb.matchPeers') : t('gauges.attract.climb.leading')
      if (!next) return t('gauges.attract.climb.leading')
      const near = next.pct - hp <= 12
      if (next.key === 'best') return near ? t('gauges.attract.climb.almostBest') : t('gauges.attract.climb.onWayBest')
      if (near) return next.key === 'peers' ? t('gauges.attract.climb.almostPeers') : t('gauges.attract.climb.almostBic')
      const reached = [...rungs].reverse().find(r => r.pct <= hp)
      return t('gauges.attract.climb.past', {
        reached: t(`gauges.attract.reached.${reached ? reached.key : 'fallback'}`),
        next: t(`gauges.attract.nextShort.${next.key === 'peers' ? 'peers' : 'bic'}`),
      })
    })
    const climbEuro = computed(() => {
      const { next, realized } = activeRung.value
      return next ? Math.max(0, (next.total || 0) - realized) : 0
    })
    // The € shown: the canonical store within-reach total when supplied (so the
    // gauge agrees with the hero + cards), else the own-best climb gap.
    const displayEuro = computed(() =>
      props.withinReachEur != null ? props.withinReachEur : climbEuro.value
    )
    const climbEuroSub = computed(() => {
      // When showing the canonical store total, it spans every zone climbing its
      // own next rung — not a single store-level rung — so keep the copy general.
      if (props.withinReachEur != null) return t('gauges.attract.euroSub.zones')
      const n = activeRung.value.next
      if (!n) return t('gauges.attract.euroSub.atBic')
      return n.key === 'best'
        ? t('gauges.attract.euroSub.matchBest')
        : t(n.key === 'peers' ? 'gauges.attract.euroSub.toPeers' : 'gauges.attract.euroSub.toBic')
    })

    // --- Previous-period % + delta ---
    const prevPctRounded = computed(() => {
      if (!hasProductZones.value) return 0
      const t = compareToTotals.value
      if (!t) return null
      const v = Number(t.efficiencyPct)
      return Number.isFinite(v) ? Math.round(Math.max(0, Math.min(100, v))) : null
    })
    const deltaPP = computed(() => {
      if (!hasProductZones.value) return 0
      const prev = prevPctRounded.value
      if (prev == null) return null
      return Math.round(headlinePctVal.value - prev)
    })
    const deltaLabel = computed(() => {
      if (deltaPP.value == null) return null
      const sign = deltaPP.value > 0 ? '+' : deltaPP.value < 0 ? '−' : '±'
      const abs  = Math.abs(Math.round(deltaPP.value))
      const arrow = deltaPP.value > 0 ? '▲' : deltaPP.value < 0 ? '▼' : '◆'
      return t('gauges.row.delta', { arrow, sign, abs, label: props.compareToLabel })
    })

    const cardDisabled = computed(
      () => !props.isActive || !hasProductZones.value || !props.productZoneCount
    )

    function formatMoney(value) {
      const v = Math.round(Number(value || 0))
      if (!Number.isFinite(v)) return '0'
      // English keeps the browser-locale grouping it always had; Dutch is 1.234.
      if (currentLanguage() !== 'en') return formatNumber(v)
      return v.toLocaleString(undefined, { maximumFractionDigits: 0 })
    }

    // --- The row's states, rungs and (possibly shared) scale ---
    const measurable = computed(() =>
      hasProductZones.value && props.hasMeasurableProductZones && !signalNotMeasurable.value)
    const rowStatus = computed(() => {
      if (!hasProductZones.value) return { title: t('gauges.row.notConfigured'), sub: t('gauges.attract.notConfiguredSub') }
      if (!measurable.value) return { title: t('gauges.attract.notMeasurable'), sub: t('gauges.attract.notMeasurableSub') }
      return null
    })
    const rowRungs = computed(() => {
      const rows = [{ key: 'best', name: t('gauges.rungs.yourBest'), pct: 100 }]
      if (peerTick.value != null) rows.push({ key: 'peers', name: t('gauges.rungs.peers'), pct: peerTick.value })
      if (ceilingTick.value != null) rows.push({ key: 'bic', name: t('gauges.rungs.bic'), pct: ceilingTick.value })
      return rows
    })
    const rowAxisMax = computed(() => props.axisMax || climbAxisMax.value)
    const rowMeta = computed(() =>
      measurable.value
        ? t('gauges.row.basedOn', {
          list: `${props.productZoneCount} <strong>${t('gauges.zoneNames.product', props.productZoneCount)}</strong>`,
        })
        : '')
    watch(
      () => (measurable.value
        ? Math.max(100, headlinePctVal.value, ceilingTick.value ?? 0, peerTick.value ?? 0)
        : null),
      (top) => emit('axis-top', top),
      { immediate: true },
    )

    return {
      rowStatus,
      rowRungs,
      rowAxisMax,
      rowMeta,
      showEfficiencyHelp,
      hasProductZones,
      headlinePctVal,
      climbCopy,
      displayEuro,
      climbEuroSub,
      deltaLabel,
      cardDisabled,
      formatMoney,
    }
  }
}
</script>
